/**
 * Действия режима «Свой текст».
 *
 * Текст принимается один раз, дальше занятие идёт как обычная тренировка:
 * один прогон — один результат.
 */

import { useCallback } from 'react';
import type { TypingSession } from '@type2x/typing-engine';
import type { Workspace } from '@type2x/feature-personal-workspace';
import type { SessionPayload } from '@type2x/feature-sync';

import type { Runtime } from '../runtime.js';
import type { AppScreen } from '../state/app-state.js';
import { customTextScreen, finishCustomText, planFromText } from '../state/custom-text.js';

/** Действия режима, которые приложение передаёт экранам. */
export interface CustomTextActions {
  readonly onStartCustomText: () => void;
  readonly onCustomTextSubmit: (text: string) => void;
  readonly onCustomTextDone: (run: TypingSession) => void;
}

/** Что нужно режиму от приложения. */
export interface CustomTextDeps {
  readonly workspace: Workspace | null;
  readonly commit: (next: Workspace) => Promise<void>;
  readonly screen: AppScreen;
  readonly setScreen: (screen: AppScreen) => void;
  readonly submit: (payload: SessionPayload) => Promise<unknown>;
}

export function useCustomText(runtime: Runtime, deps: CustomTextDeps): CustomTextActions {
  const { workspace, commit, screen, setScreen, submit } = deps;

  const onStartCustomText = useCallback(() => {
    setScreen(customTextScreen(runtime));
  }, [runtime, setScreen]);

  const onCustomTextSubmit = useCallback(
    (text: string) => {
      if (workspace === null) return;
      setScreen(planFromText(runtime, workspace, text));
    },
    [runtime, workspace, setScreen],
  );

  const onCustomTextDone = useCallback(
    (run: TypingSession) => {
      if (workspace === null || screen.name !== 'custom-text' || screen.plan === null) return;

      const transition = finishCustomText(runtime, workspace, screen.plan, run, screen.sessionId);
      void commit(transition.workspace);
      setScreen(transition.screen);

      // Отправка идёт после показа результата: сеть не задерживает экран.
      if (transition.sessionPayload !== undefined) {
        void submit(transition.sessionPayload);
      }
    },
    [runtime, workspace, screen, commit, setScreen, submit],
  );

  return { onStartCustomText, onCustomTextSubmit, onCustomTextDone };
}
